import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogClose
} from '@/components/ui/dialog'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { qrService } from '@/lib/qr'
import { createEthereumURI } from '@/utils'
import { Copy, Share2, AlertTriangle, X, ChevronLeft } from 'lucide-react'

interface ReceiveTokenModalProps {
	isOpen: boolean
	onClose: () => void
}

export default function ReceiveTokenModal({ isOpen, onClose }: ReceiveTokenModalProps) {
	const [walletQR, setWalletQR] = useState<string | null>(null)
	const { toast } = useToast()
	const { walletAddress } = useUser()

	const copyAddress = async (address: string) => {
		try {
			await navigator.clipboard.writeText(address)
			toast({
				title: 'Address Copied',
				description: 'Address has been copied to clipboard'
			})
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to copy address',
				variant: 'destructive'
			})
		}
	}

	const shareAddress = async () => {
		if (walletAddress && navigator.share) {
			try {
				await navigator.share({
					title: 'My Wallet Address',
					text: walletAddress
				})
			} catch (error) {
				// Fallback to copy if sharing fails
				copyAddress(walletAddress)
			}
		} else if (walletAddress) {
			copyAddress(walletAddress)
		}
	}

	const generateQR = async () => {
		if (walletAddress) {
			try {
				const qrPayload = createEthereumURI(walletAddress)
				const qrCode = await qrService.generateQR(qrPayload)
				setWalletQR(qrCode)
			} catch (error) {
				console.error('Error generating QR code:', error)
				toast({
					title: 'QR Code Generation Failed',
					description: 'Failed to generate QR code. Please try again.',
					variant: 'destructive'
				})
			}
		}
	}

	useEffect(() => {
		if (isOpen && walletAddress && !walletQR) {
			generateQR()
		}
	}, [isOpen, walletAddress])

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="h-full w-full p-5 overflow-y-auto sm:overflow-visible sm:rounded-lg rounded-lg bg-[#18181B] border-0">
				<DialogClose asChild>
					<button
						className="absolute top-4 left-5 max-[764px]:right-[-10px] w-8 h-8 flex items-center justify-center rounded-full text-[#ffffff] bg-white/10 hover:bg-white/20 transition p-0 m-0"
						aria-label="Close">
						<ChevronLeft className="h-5 w-5 text-[#ffffff]" />
					</button>
				</DialogClose>
				<DialogHeader>
					<DialogTitle className="text-[16px] leading-[18px] mt-1 mb-2 font-semibold font-figtree text-[#ffffff]">Receive Tokens</DialogTitle>
					<DialogDescription className="text-xs leading-4 font-medium font-figtree mt-0 text-[#BABABB]">
						Share your wallet address to receive tokens.
					</DialogDescription>
				</DialogHeader>
				<div className="">
					{/* QR Code Display */} 
					{walletAddress && walletQR && (
						<div className="text-center flex flex-col justify-between h-full max-w-[380px] mx-auto">

							<div className="bg-[#212124] rounded-[16px] px-9 pt-[70px] pb-6">
								<Card className="bg-white border-0 rounded-[16px] shadow-none">
									<img
										src={walletQR}
										alt="Wallet Address QR Code"
										className="w-full h-full mx-auto rounded-[16px]"
									/>
								</Card>
								<p className="text-sm font-medium text-start leading-4 font-figtree text-[#ffffff] mb-1 mt-6">Your Wallet Address:</p>
								<div className="w-full text-start">
									<span className="wr-address-pop text-xs leading-4 font-normal font-figtree text-[#BABABB]">{walletAddress}</span>
								</div>
							</div>
							{/* Important Note */}
							{/* <div className="bg-[#3C3200] border border-[#6B5900] rounded-lg p-4">
								<div className="flex items-start gap-3">
									<AlertTriangle className="h-5 w-5 text-[#FACC15] mt-0.5 flex-shrink-0" />
									<div>
										<h4 className="font-medium text-[#FDE047] mb-1">Important Note</h4>
										<p className="text-sm text-[#FEF08A]">
											Only send Arbitrum network assets to this address. Sending assets from other networks may result in permanent loss.
										</p>
									</div>
								</div>
							</div> */}
							<div className="flex gap-2">
								<Button
									variant="default"
									className="flex-1 bg-[#EF4444] h-[42px] rounded-[21px] shadow-[0px_4px_36px_0px_#DC262699]"
									onClick={() => copyAddress(walletAddress)}
								>
									<Copy className="h-4 w-4 mr-2" />
									Copy Address
								</Button>
								{/* <Button
										variant="outline"
										className="flex-1 bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C] hover:text-[#ffffff]"
										onClick={shareAddress}
									>
										<Share2 className="h-4 w-4 mr-2" />
										Share
									</Button> */}
							</div>
						</div>
					)}

					{/* Connected wallet address display (fallback if no QR) */}
					{walletAddress && !walletQR && (
						<div className="text-center p-4 space-y-2 bg-[#2F2F32] rounded-lg border border-[#4C4C4C]">
							<p className="text-sm text-[#BABABB] font-medium">
								Your Connected Wallet:
							</p>
							<div className="flex items-center justify-center gap-2 flex-wrap">
								<code className="block p-2 bg-[#3C3C3C] rounded break-all text-sm text-[#ffffff]">
									{walletAddress}
								</code>
								<Button
									variant="outline"
									size="sm"
									className="bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C] hover:text-[#ffffff]"
									onClick={() => {
										if (walletAddress) {
											copyAddress(walletAddress)
										}
									}}>
									<Copy className="h-4 w-4 mr-1" />
									Copy
								</Button>
							</div>
						</div>
					)}


				</div>
			</DialogContent>
		</Dialog>
	)
}